import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import { Bug, Loader2, Camera, Send } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API_BASE } from '../config/api';

// Collect recent console errors in the background
const consoleErrors = [];
const MAX_ERRORS = 30;

if (typeof window !== 'undefined' && !window.__bugReportErrorHook) {
  window.__bugReportErrorHook = true;
  const origError = console.error;
  console.error = function (...args) {
    origError.apply(console, args);
    const msg = args.map(a => (typeof a === 'object' ? JSON.stringify(a).slice(0, 300) : String(a).slice(0, 300))).join(' ');
    consoleErrors.push(`[error] ${msg}`);
    if (consoleErrors.length > MAX_ERRORS) consoleErrors.shift();
  };
  const origWarn = console.warn;
  console.warn = function (...args) {
    origWarn.apply(console, args);
    const msg = args.map(a => (typeof a === 'object' ? JSON.stringify(a).slice(0, 300) : String(a).slice(0, 300))).join(' ');
    consoleErrors.push(`[warn] ${msg}`);
    if (consoleErrors.length > MAX_ERRORS) consoleErrors.shift();
  };
  window.addEventListener('error', (e) => {
    consoleErrors.push(`[uncaught] ${e.message} at ${e.filename}:${e.lineno}`);
    if (consoleErrors.length > MAX_ERRORS) consoleErrors.shift();
  });
  window.addEventListener('unhandledrejection', (e) => {
    consoleErrors.push(`[promise] ${String(e.reason).slice(0, 300)}`);
    if (consoleErrors.length > MAX_ERRORS) consoleErrors.shift();
  });
}

const BugReportButton = () => {
  const [open, setOpen] = useState(false);
  const [description, setDescription] = useState('');
  const [includeScreenshot, setIncludeScreenshot] = useState(true);
  const [screenshot, setScreenshot] = useState(null);
  const [capturing, setCapturing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const buttonRef = useRef(null);

  const captureScreenshot = useCallback(async () => {
    setCapturing(true);
    try {
      // Hide the bug report button during capture
      if (buttonRef.current) buttonRef.current.style.display = 'none';
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(document.body, {
        scale: 0.5,
        useCORS: true,
        logging: false,
        ignoreElements: (el) => el.id === 'bug-report-dialog',
      });
      const dataUrl = canvas.toDataURL('image/jpeg', 0.6);
      setScreenshot(dataUrl);
    } catch (e) {
      console.warn('Screenshot capture failed:', e);
      setScreenshot(null);
    } finally {
      if (buttonRef.current) buttonRef.current.style.display = '';
      setCapturing(false);
    }
  }, []);

  useEffect(() => {
    if (open && includeScreenshot && !screenshot) {
      captureScreenshot();
    }
  }, [open, includeScreenshot, screenshot, captureScreenshot]);

  const handleOpen = () => {
    setDescription('');
    setScreenshot(null);
    setOpen(true);
  };

  const handleSubmit = async () => {
    if (!description.trim()) {
      toast.error('Please describe the issue');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        description: description.trim(),
        page_url: window.location.href,
        browser_info: navigator.userAgent,
        screen_size: `${window.innerWidth}x${window.innerHeight}`,
        console_errors: [...consoleErrors],
        screenshot: includeScreenshot ? screenshot : null,
      };
      await axios.post(`${API_BASE}/bug-reports`, payload, { withCredentials: true });
      toast.success('Bug report submitted. Thank you!');
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit report');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        ref={buttonRef}
        onClick={handleOpen}
        className="fixed bottom-5 left-5 z-50 flex items-center gap-2 rounded-full bg-background-paper border border-border px-4 py-2.5 text-sm text-foreground-muted hover:text-foreground hover:border-primary/50 transition-colors shadow-lg"
        data-testid="bug-report-trigger"
      >
        <Bug className="w-4 h-4" />
        <span className="hidden sm:inline">Report Bug</span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent id="bug-report-dialog" className="bg-background-paper border-border max-w-lg" data-testid="bug-report-dialog">
          <DialogHeader>
            <DialogTitle className="text-foreground flex items-center gap-2">
              <Bug className="w-5 h-5 text-primary" />
              Report a Bug
            </DialogTitle>
            <DialogDescription className="text-foreground-muted">
              Describe what went wrong. Debug info is attached automatically.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-foreground">What happened?</Label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe the issue you encountered..."
                className="bg-background border-border min-h-[100px] resize-none"
                maxLength={2000}
                autoFocus
                data-testid="bug-report-description"
              />
              <p className="text-xs text-foreground-subtle text-right">{description.length}/2000</p>
            </div>

            {/* Screenshot toggle + preview */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Camera className="w-4 h-4 text-foreground-muted" />
                  <Label className="text-foreground text-sm">Include screenshot</Label>
                </div>
                <Switch
                  checked={includeScreenshot}
                  onCheckedChange={(v) => {
                    setIncludeScreenshot(v);
                    if (v && !screenshot) captureScreenshot();
                  }}
                  data-testid="bug-report-screenshot-toggle"
                />
              </div>
              {includeScreenshot && (
                <div className="rounded-md border border-border overflow-hidden bg-background">
                  {capturing ? (
                    <div className="flex items-center justify-center h-32 text-foreground-muted text-sm">
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Capturing screenshot...
                    </div>
                  ) : screenshot ? (
                    <img src={screenshot} alt="Screenshot preview" className="w-full max-h-48 object-contain" data-testid="bug-report-screenshot-preview" />
                  ) : (
                    <div className="flex items-center justify-center h-32 text-foreground-muted text-sm">
                      Screenshot unavailable
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Auto-collected info badge */}
            <div className="rounded-md bg-background border border-border p-3">
              <p className="text-xs text-foreground-subtle mb-1.5 font-medium">Automatically included:</p>
              <div className="flex flex-wrap gap-1.5">
                {['Page URL', 'Browser info', 'Screen size', `${consoleErrors.length} console log(s)`].map((item) => (
                  <span key={item} className="inline-block text-xs bg-primary/10 text-primary/80 rounded px-2 py-0.5">{item}</span>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-border">Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting || !description.trim()} className="bg-primary hover:bg-primary/90" data-testid="bug-report-submit">
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Submit Report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BugReportButton;
